/*
 * This file is part of glowingblue/orell-rezensionen.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import ReviewFeatureAlertBody from '../components/ReviewFeatureAlertBody';
import { slug } from '../../common';
import type { AlertIdentifier } from 'flarum/common/states/AlertManagerState';

const MAX_RETRIES = 60; // 10 minutes at the retry interval below

let retryCount = 0;

/** `PageState#data` is typed `{}` by core; only the route name is read here. */
type CurrentPageData = { routeName?: string };

const shouldDelayAlert = () => {
	if (!app.session.user || !(app.current?.data as CurrentPageData | undefined)?.routeName) {
		return true;
	}

	const pageIdParam = m.route.param('id');
	const welcomePageId = app.forum.attribute<string>('glowingblue-welcome-page.page_id');

	if ((app.current.data as CurrentPageData).routeName === 'page' && parseInt(pageIdParam) === parseInt(welcomePageId)) {
		return true;
	}

	// Wait until the user is through the home tour.
	if (!app.session.user.preferences()?.homeTourDisabled) {
		return true;
	}

	return false;
};

const showReviewFeatureAlert = () => {
	if (!app.forum.attribute(`${slug}.review_feature_alert_enabled`)) {
		return;
	}

	if (shouldDelayAlert()) {
		if (retryCount < MAX_RETRIES) {
			retryCount++;
			setTimeout(showReviewFeatureAlert, 10000);
		}
		return;
	}

	retryCount = 0;

	// `shouldDelayAlert()` already returned early for a logged-out user.
	if (app.session.user!.preferences()!.review_feature_alert_seen) {
		return;
	}

	let waitTime = app.forum.attribute<number>(`${slug}.review_feature_alert_delay`) || 30;
	waitTime *= 1000;

	setTimeout(() => {
		// The user may have logged out in the meantime.
		if (!app.session.user) {
			return;
		}

		let alertId: AlertIdentifier | undefined;
		const dismiss = (hide = false, fromState = false) => {
			if (alertId) {
				if (hide) {
					// Only reachable once the alert was shown, which required a logged-in user.
					app.session.user!.savePreferences({
						review_feature_alert_seen: true,
					});
				}
				if (!fromState) {
					app.alerts.dismiss(alertId);
				}

				delete app.gbDismissReviewFeatureAlert;
			}
		};

		// Also dismissed from elsewhere, hence the global handle.
		app.gbDismissReviewFeatureAlert = dismiss;

		alertId = app.alerts.show({ type: 'reviewFeature' }, <ReviewFeatureAlertBody dismiss={dismiss} />);

		app.reviewFeatureAlertId = alertId;
	}, waitTime);
};

export default function () {
	extend(app, 'mount', showReviewFeatureAlert);

	extend(app.alerts, 'dismiss', function (_, key) {
		if (app.gbDismissReviewFeatureAlert && `${key}` === `${app.reviewFeatureAlertId}`) {
			app.gbDismissReviewFeatureAlert(true, true);
		}
	});
}
